// @flow
import * as React from 'react';
import Modal from 'react-modal';

import AdminAutoMessageSftpCredentialsModal from 'admin/components/AdminAutoMessageSftpCredentialsModal';
import assertions from 'lib/assertions';
import Button from 'components/Button';
import Container from 'components/Container';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Link from 'components/Link';
import List from 'components/List';
import ListItem from 'components/ListItem';
import MediaObject from 'components/MediaObject';
import Notice from 'components/Notice';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import View from 'components/View';
import WizardHelperText from 'components/WizardHelperText';
import WizardPageLayout from 'components/WizardPageLayout';

import type { AutoMessageTabKey } from './';
import type {
  AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials,
  SaveAutoMessageInput,
} from 'admin/components/AdminAutoMessageSftpCredentials/queries/__generated__/AdminAutoMessageSftpCredentials';
import type { GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_creatorOrganization } from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';

type Props = {
  creatorOrganization: GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_creatorOrganization,
  csvName: ?string,
  sftpCredentials: ?AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials,
  possibleSftpCredentials: AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials[],
  onChangeField: () => void,
  onAdvance: () => void,
  onBack: () => void,
  onSave: (fields: SaveAutoMessageInput, onSuccess: Function) => void,
  onJump: (key: AutoMessageTabKey) => void,
};

type State = {
  sftpCredentialsUsernameFromField: ?string,
  sftpCredentialsDialog: ?('create' | 'edit'),
};

class AdminNewAutoMessageSyncData extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      sftpCredentialsUsernameFromField:
        props.sftpCredentials && props.sftpCredentials.credentials.username,
      sftpCredentialsDialog: undefined,
    };
  }

  componentDidMount() {
    EventTracking.trackKey('auto_messaging.setup_credentials.view');
    if (this.props.sftpCredentials) {
      this.setSftpCredentialsName(this.props.sftpCredentials.credentials.username);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.sftpCredentials && this.props.sftpCredentials) {
      this.setSftpCredentialsName(this.props.sftpCredentials.credentials.username);
    }
  }

  showCreateDialog = () => {
    this.setState({ sftpCredentialsDialog: 'create' });
  };

  showEditDialog = () => {
    this.setState({ sftpCredentialsDialog: 'edit' });
  };

  hideDialog = () => {
    this.setState({ sftpCredentialsDialog: undefined });
  };

  setSftpCredentialsName = (sftpCredentialsName: ?string) => {
    this.setState({ sftpCredentialsUsernameFromField: sftpCredentialsName });
  };

  handleSftpCredentialsChange = (sftpCredentialsName: ?string) => {
    this.setSftpCredentialsName(sftpCredentialsName);
    this.props.onChangeField();
  };

  renderAddButtonOrDropdown = () => {
    const { possibleSftpCredentials } = this.props;
    const { sftpCredentialsUsernameFromField } = this.state;
    const credentialsOptions = possibleSftpCredentials.map(credentials => ({
      label: credentials.displayName,
      value: credentials.credentials.username,
    }));
    const dropdown = (
      <Container size="small" flush align="left">
        <FormElement
          label="Credentials"
          validate={value => assertions.notEmpty(value, 'Please select a set of credentials')}
          onValidChange={value => this.handleSftpCredentialsChange(value)}
          onInvalidChange={() => this.handleSftpCredentialsChange(undefined)}
          subtext={
            <Text>
              or <Link onClick={this.showCreateDialog}>create a new set of credentials</Link>
            </Text>
          }
          element={Select}
          elementProps={{
            value: sftpCredentialsUsernameFromField,
            placeholder: 'Select credentials',
            name: 'org',
            options: credentialsOptions,
            searchable: false,
          }}
          appendedButton={
            sftpCredentialsUsernameFromField ? (
              <Button level="secondary" onClick={this.showEditDialog}>
                View
              </Button>
            ) : null
          }
        />
      </Container>
    );
    const button = (
      <Button data-test="add-credentials-button" onClick={this.showCreateDialog} level="secondary">
        Add a new set of credentials
      </Button>
    );

    return credentialsOptions.length ? dropdown : button;
  };

  render() {
    const { onSave, csvName, creatorOrganization, possibleSftpCredentials } = this.props;
    const { sftpCredentialsUsernameFromField, sftpCredentialsDialog } = this.state;
    const selectedSftpCredentials = possibleSftpCredentials.find(
      credentials => credentials.credentials.username === sftpCredentialsUsernameFromField
    );

    return (
      <React.Fragment>
        <Modal isOpen={Boolean(sftpCredentialsDialog)}>
          <AdminAutoMessageSftpCredentialsModal
            onCredentialGeneration={sftpCredentials =>
              this.handleSftpCredentialsChange(sftpCredentials.credentials.username)
            }
            onCredentialDeletion={() => {
              this.hideDialog();
              this.showCreateDialog();
            }}
            onClose={this.hideDialog}
            credentials={sftpCredentialsDialog === 'create' ? undefined : selectedSftpCredentials}
            orgUuid={creatorOrganization.uuid}
          />
        </Modal>
        <WizardPageLayout
          backButton={
            <Button level="secondary" onClick={this.props.onBack}>
              Back
            </Button>
          }
          actionButtons={[
            <Button
              data-test="continue-button"
              onClick={() =>
                onSave(
                  { sftpCredentialsUsername: sftpCredentialsUsernameFromField },
                  this.props.onAdvance
                )
              }
              disabled={!sftpCredentialsUsernameFromField}>
              Save and continue
            </Button>,
          ]}>
          <View>
            <Row size="xxxxxlarge">
              <Container flush size="large">
                <Text align="left" lineHeight="large">
                  <Row size="small">
                    <Heading level="2">
                      <Text weight="semibold">Set up file transfer to Remind via SFTP</Text>
                    </Heading>
                  </Row>
                  <MediaObject center={false} figure="right">
                    <Container flush size="large">
                      <List marker="number" size="xlarge" markerPosition="outside">
                        <ListItem>
                          <Row size="small">
                            Enter the following credentials directly into your SIS or FTP client.
                            Your SIS or FTP client must support SFTP for transferring files to
                            third-party systems.
                          </Row>
                          <Row size="small">
                            <Notice glyph="explanation" color="brand" noShadow full>
                              <Text size="xsmall">
                                These credentials are different from the ones you may have used when
                                you rostered with Remind.
                              </Text>
                            </Notice>
                          </Row>
                          {this.renderAddButtonOrDropdown()}
                        </ListItem>
                        <ListItem>
                          <Row size="small">
                            In your SIS or FTP client, schedule a recurring file transfer
                            {csvName ? (
                              <React.Fragment>
                                {' '}
                                for <Text element="code">{csvName}</Text>
                              </React.Fragment>
                            ) : (
                              ''
                            )}
                            . This determines the send time and frequency for your auto message.
                          </Row>
                          <Row size="small">
                            Auto messages will not be sent between 12 a.m. ET and 7 a.m. ET. This is
                            to prevent auto messages from being sent during late hours.
                          </Row>
                          Once you activate your auto message, it will be sent every time{' '}
                          {csvName ? <Text element="code">{csvName}</Text> : 'your file'} is
                          transferred to Remind.
                        </ListItem>
                      </List>
                    </Container>
                    <WizardHelperText
                      text="Not comfortable transferring files via SFTP?"
                      linkLabel="Skip to compose"
                      onClickLink={() => this.props.onJump('compose')}
                    />
                  </MediaObject>
                </Text>
              </Container>
            </Row>
          </View>
        </WizardPageLayout>
      </React.Fragment>
    );
  }
}

export default AdminNewAutoMessageSyncData;
